import {
  languagesForCountries,
  VOICE_STYLES,
  VOICES,
} from './aiAssistantCatalog';
import {
  AssistantGender,
  AssistantStatus,
  VoiceAssistantRecord,
  VoiceLanguageSettings,
} from '../../services/voiceAssistantService';

export interface AssistantFormState {
  gender: AssistantGender;
  multilingualEnabled: boolean;
  countries: string[];
  languages: string[];
  provider: string;
  model: string;
  ttsLanguage: string;
  voice: string;
  speed: number;
  voiceStyle: string;
  task: string;
  industry: string;
  niche: string;
  status: 'Active' | 'InActive';
}

const FRIENDLY_STYLE = VOICE_STYLES[0].label;

export const emptyAssistantForm = (): AssistantFormState => ({
  gender: 'female',
  multilingualEnabled: false,
  countries: [],
  languages: ['en-US'],
  provider: 'google-cloud',
  model: 'standard',
  ttsLanguage: 'en-IN',
  voice: 'en-IN-Standard-A',
  speed: 1,
  voiceStyle: FRIENDLY_STYLE,
  task: '',
  industry: '',
  niche: '',
  status: 'InActive',
});

const clampSpeed = (value: number): number => {
  const rounded = Math.round(value * 10) / 10;
  return Math.min(2, Math.max(0.5, rounded));
};

export const formFromConfig = (config: VoiceAssistantRecord): AssistantFormState => {
  const settings = config.voice_language_settings || {};
  const tts = settings.tts || {};
  const gender: AssistantGender = tts.gender === 'male' ? 'male' : 'female';
  const knownVoice = VOICES.find((voice) => voice.id === tts.voice_name);
  const fallbackVoice = VOICES.find((voice) => voice.gender === gender)?.id || 'Achernar';
  const speed = typeof tts.speaking_rate === 'number' && tts.speaking_rate >= 0.5 && tts.speaking_rate <= 2
    ? tts.speaking_rate
    : 1;

  return {
    gender: knownVoice?.gender || gender,
    multilingualEnabled: Boolean(settings.multilingual_mode?.enabled),
    countries: Array.isArray(settings.target_countries) ? settings.target_countries : [],
    languages: settings.languages?.length ? settings.languages : ['en-US'],
    provider: tts.provider || 'SAiTTS v1',
    model: tts.model_name || 'SAiTTS v1 — Tier 1',
    ttsLanguage: tts.language_code || 'en-IN',
    voice: tts.voice_name || fallbackVoice,
    speed,
    voiceStyle: settings.voice_style || FRIENDLY_STYLE,
    task: settings.task || '',
    industry: settings.industry || '',
    niche: settings.niche || '',
    status: config.status === 'Active' ? 'Active' : 'InActive',
  };
};

export const buildVoiceLanguageSettings = (
  form: AssistantFormState,
  existing?: VoiceLanguageSettings
): VoiceLanguageSettings => {
  const style = VOICE_STYLES.find((item) => item.label === form.voiceStyle);
  const tts = existing?.tts || {};
  const stt = existing?.stt || {};
  const primaryLanguage = form.languages[0] || stt.language_code || 'en-IN';
  const prompt = style?.prompt
    || (existing?.voice_style === form.voiceStyle ? tts.prompt : '')
    || tts.prompt
    || 'Speak in a friendly, warm, approachable tone.';

  return {
    target_countries: form.countries,
    languages: form.languages,
    task: form.task,
    industry: form.industry,
    niche: form.niche,
    voice_style: form.voiceStyle,
    multilingual_mode: {
      enabled: form.multilingualEnabled,
      alternative_languages: existing?.multilingual_mode?.alternative_languages?.length
        ? existing.multilingual_mode.alternative_languages
        : ['hi-IN'],
    },
    stt: {
      language_code: primaryLanguage,
      encoding: stt.encoding || 'WEBM_OPUS',
      model: stt.model || 'latest_long',
      enable_automatic_punctuation: stt.enable_automatic_punctuation !== false,
      max_alternatives: stt.max_alternatives || 1,
    },
    tts: {
      provider: form.provider,
      language_code: form.ttsLanguage,
      voice_name: form.voice,
      model_name: form.model,
      prompt,
      audio_encoding: tts.audio_encoding || 'MP3',
      speaking_rate: clampSpeed(form.speed),
      pitch: typeof tts.pitch === 'number' ? tts.pitch : 0,
      gender: form.gender,
      voice_by_gender: {
        male: form.gender === 'male' ? form.voice : (tts.voice_by_gender?.male || 'en-IN-Standard-B'),
        female: form.gender === 'female' ? form.voice : (tts.voice_by_gender?.female || 'en-IN-Standard-A'),
      },
      fallback_voice: tts.fallback_voice || 'en-IN-Standard-B',
    },
  };
};

export const stepError = (step: number, form: AssistantFormState): string => {
  if (step === 0) {
    if (!form.languages.length) return 'Select at least one language.';
    const allowed = languagesForCountries(form.countries).map((language) => language.code);
    const outsideFilter = form.countries.length > 0 && form.languages.some((code) => !allowed.includes(code));
    if (outsideFilter) return 'Choose languages available for the selected countries.';
    if (!form.provider || !form.model || !form.ttsLanguage) return 'Select a TTS provider, model and language.';
    if (!form.voice) return 'Select a voice.';
    if (!form.voiceStyle) return 'Select a voice style.';
    return '';
  }
  if (step === 1 && !form.task) return 'Select what your AI Assistant should do.';
  if (step === 2 && !form.industry) return 'Select an industry.';
  if (step === 3 && !form.niche) return 'Select a niche.';
  return '';
};

export const firstInvalidStep = (form: AssistantFormState): { step: number; message: string } | null => {
  for (let step = 0; step <= 3; step += 1) {
    const message = stepError(step, form);
    if (message) return { step, message };
  }
  return null;
};

export const assistantStatusFromForm = (status: AssistantFormState['status']): AssistantStatus => status;
